import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconArrowLeft, IconCopy, IconPencil } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { App } from "./types";
import { STATUS_COLORS } from "./types";

interface AppHeaderProps {
  app: App;
  appId: string;
  onEditListing: () => void;
}

export function AppHeader({ app, appId, onEditListing }: AppHeaderProps) {
  const navigate = useNavigate();

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="md">
          <ActionIcon
            variant="subtle"
            onClick={() => navigate("/developer/published")}
            size="lg"
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          {app.icon && <Avatar src={app.icon} size={56} radius="md" />}
          <Stack gap={2}>
            <Group gap="sm">
              <Title order={3}>{app.name}</Title>
              <Badge
                color={STATUS_COLORS[app.status] ?? "gray"}
                variant="light"
              >
                {app.status}
              </Badge>
              <Text size="sm" c="dimmed">
                v{app.version}
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lineClamp={1}>
              {app.shortDescription}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconPencil size={14} />}
            onClick={onEditListing}
          >
            Edit Listing
          </Button>
          <Button
            variant="light"
            onClick={() => navigate(`/developer/apps/${appId}/releases`)}
          >
            Manage Releases
          </Button>
        </Group>
      </Group>

      {/* IDs Row */}
      <Group gap="lg" ml={52}>
        <Group gap={4}>
          <Text size="xs" c="dimmed">
            App ID:
          </Text>
          <Code style={{ fontSize: 11 }}>{appId}</Code>
          <CopyButton value={appId}>
            {({ copy, copied }) => (
              <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
                <ActionIcon size="xs" variant="subtle" onClick={copy}>
                  <IconCopy size={12} />
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        <Group gap={4}>
          <Text size="xs" c="dimmed">
            Org App ID:
          </Text>
          <Code style={{ fontSize: 11 }}>{app.organizationAppId}</Code>
          <CopyButton value={app.organizationAppId}>
            {({ copy, copied }) => (
              <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
                <ActionIcon size="xs" variant="subtle" onClick={copy}>
                  <IconCopy size={12} />
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      </Group>
    </Stack>
  );
}
